<template>
    <div class="page-container">
        <Headbar>
            <template v-slot:left>
                <Button className="--primary --small" :onclick="toggleCreate">
                    {{ $t('suppliers.add_supplier') }}
                </Button>
                <Button className="--primary --outline --small" :onclick="exportData" :disabled="is_exporting">
                    Export
                </Button>
            </template>
            <template v-slot:right>
                <Search class="search-desktop" :placeholder="$t('suppliers.search_suppliers')" @search="search"/>
                <button class="btn-search-mobile btn-icon-only"
                        @click="headbarExpanded = headbarExpanded === 'search' ? null : 'search'">
                    <font-awesome-icon v-if="headbarExpanded === 'search'" :icon="['far', 'times']"/>
                    <font-awesome-icon v-else :icon="['far', 'search']"/>
                </button>
            </template>
            <template v-slot:expanded>
                <div class="headbar-expanded-container">
                    <Search v-if="headbarExpanded === 'search'" :placeholder="$t('suppliers.search_suppliers')" @search="search"/>
                </div>
            </template>
        </Headbar>
        <main>
            <vue-good-table
                mode="remote"
                styleClass="vgt-table vgt-custom"
                :columns="columns"
                :rows="suppliers"
                :isLoading.sync="is_loading_suppliers"
                :search-options="{
                    enabled: false,
                }"
                :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    dropdownAllowAll: false,
                    perPage: 25,
                    perPageDropdownEnabled: false,
                    rowsPerPageLabel: $t('x_per_page', {x: $t('suppliers.suppliers')}),
                }"
                :sort-options="{
                  enabled: true,
                  multipleColumns: true,
                  initialSortBy: {field: 'attributes.name', type: 'asc'}
                }"
                :totalRows="totalRecords"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange">
                <template slot="table-row" slot-scope="props">
                    <div v-if="props.column.field === 'after'" class="td-after">
                            <Button className="--secondary --outline --mini --big-text"
                                    :onclick="()=>toggleUpdate(props.row)">
                                <font-awesome-icon :icon="['fal', 'pencil']"/>
                            </Button>
                        <Button className="--secondary --outline --mini --big-text"
                                :onclick="()=>toggleDelete(props.row)">
                            <font-awesome-icon :icon="['fal', 'trash']"/>
                        </Button>
                    </div>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </main>
    </div>
</template>

<script>
import Headbar from "../../components/headbar/Headbar";
import Search from "../../components/Search";
import Button from "../../components/Button";
import Select from "../../components/form/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import SuppliersCreateModal from "../../components/suppliers/SuppliersCreateModal";
import SuppliersUpdateModal from "../../components/suppliers/SuppliersUpdateModal";

export default {
    name: "suppliers-index-page",
    components: {ConfirmModal, Select, Button, Search, Headbar},
    data: function () {
        const columns = [
            {
                label: this.$t('suppliers.name'),
                field: 'attributes.name',
                sortable: true,
            },
            {
                label: this.$t('suppliers.phone'),
                field: 'attributes.phone',
                sortable: true,
            },
        ];

        if (this.$store.getters.hasPermission('update suppliers'))
            columns.push({
                label: this.$t('suppliers.actions'),
                field: 'after',
                tdClass: 'td-after',
                sortable: false
            });

        return {
            columns: columns,
            suppliers: [],
            is_loading_suppliers: false,
            totalRecords: null,
            serverParams: {sorting: []},
            searchTerm: null,
            is_deleting: false,
            headbarExpanded: false,
            is_exporting: false
        }
    },
    methods: {
        async exportData() {
            this.is_exporting = true;

            try {
                const response = await this.$axios.get('suppliers/export', {
                    params: this.serverParams,
                    responseType: 'blob'
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const now = this.$moment();
                const filename = `suppliers-${now.format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;

                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                this.$notify({
                    title: this.$t('error'),
                    text: this.$t('suppliers.error_export'),
                    type: 'error'
                });
            } finally {
                this.is_exporting = false;
            }
        },
        toggleCreate() {
            this.$modal.show(
                SuppliersCreateModal, {},
                {
                    name: 'suppliers-create-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveSuppliers();
                    }
                }
            );
        },
        toggleUpdate(supplier) {
            this.$modal.show(
                SuppliersUpdateModal,
                {
                    supplier_id: supplier.id,
                },
                {
                    name: 'suppliers-update-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.retrieveSuppliers();
                    }
                }
            );
        },
        toggleDelete(supplier) {
            this.$modal.show(
                ConfirmModal, {
                    title: this.$t('suppliers.delete_supplier'),
                    message: this.$t('suppliers.prompt_delete'),
                    confirmText: this.$t('delete'),
                    cancelText: this.$t('cancel'),
                    confirmClass: '--primary',
                    cancelClass: '--secondary --outline'
                },
                {
                    name: 'confirm-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true) {
                            this.is_deleting = true;
                            this.$axios.delete(`suppliers/${supplier.id}`)
                                .then(({data}) => {
                                    this.is_deleting = false;
                                    this.retrieveSuppliers();
                                })
                                .catch(e => {
                                    this.is_deleting = false;

                                    this.$notify({
                                        title: this.$t('error'),
                                        text: this.$larerror(e.response.data, this.$t('suppliers.error_delete')),
                                        type: 'error',
                                    });
                                });
                        }
                    }
                }
            );
        },
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        onPageChange(params) {
            this.updateParams({page: params.currentPage});
            this.retrieveSuppliers();
        },
        onSortChange(params) {
            if (!params || !params.length) {
                this.updateParams({sorting: []});
                return this.retrieveSuppliers();
            }

          let sort_by = null;
          let sort_order = params[0].type;

          params.forEach(p => {
            if (p.field === 'attributes.phone')
              sort_by = 'phone';
            else if (p.field === 'attributes.name')
              sort_by = 'name';
            else {
              sort_by = null;
            }
          });

          if (sort_order === 'none') {
            this.removeParam('sort_by');
            this.removeParam('sort_order');
          } else {
            this.updateParams({sort_by: sort_by, sort_order: sort_order});
          }
            this.retrieveSuppliers();
        },
        removeParam(param) {
            this.$delete(this.serverParams, param);
        },
        search(searchTerm) {
            this.searchTerm = searchTerm;

            if (searchTerm && searchTerm.length)
                this.updateParams({term: searchTerm});
            else this.removeParam('term');

            this.retrieveSuppliers();
        },
        retrieveSuppliers() {
            this.is_loading_suppliers = true;

            const encodedSorting = this.serverParams.sorting.map(f => btoa(JSON.stringify(f)));

            this.$axios.get('suppliers', {params: {...this.serverParams, sorting: encodedSorting}})
                .then(({data}) => {
                    this.suppliers = data.data;
                    this.totalRecords = data.meta.total;
                    this.is_loading_suppliers = false;
                })
                .catch(e => {
                    this.is_loading_suppliers = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('suppliers.error_retrieve')),
                        type: 'error',
                    });
                });
        }
    },

    mounted() {
        this.retrieveSuppliers();
    },

    head() {
        return {
            title: {
                inner: this.$t('nav.suppliers')
            },
        }
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    .headbar-expanded-container {
        @apply mt-4 flex flex-col gap-y-4 items-center;

        @screen md {
            @apply hidden;
        }
    }

    .select-dropdown {
        @apply min-w-40;
    }

    .btn-search-mobile, .btn-filter-mobile {
        @apply block text-primary w-6;

        @screen md {
            @apply hidden;
        }

        &:active, &:focus {
            @apply outline-none;
        }
    }

    .search-desktop, .filter-desktop {
        @apply hidden;

        @screen md {
            @apply block;
        }
    }

    .search-container {
        @apply mr-0;
    }

    .filter-mobile {
        @apply max-w-xs;
    }

    .td-after {
        @apply flex flex-row;

        & > * {
            @apply mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
